import type { CellMap } from "@/types/office";

const COLUMN_LABELS = ["A", "B", "C", "D", "E", "F", "G", "H"];

export function columnLabel(index: number): string {
  return COLUMN_LABELS[index] ?? String.fromCharCode(65 + index);
}

export function cellRefOf(col: number, row: number): string {
  return `${columnLabel(col)}${row + 1}`;
}

export function parseCellRef(ref: string): { col: number; row: number } | null {
  const match = /^([A-Z])([1-9][0-9]*)$/.exec(ref.toUpperCase());
  if (!match) return null;
  return { col: COLUMN_LABELS.indexOf(match[1]), row: Number(match[2]) - 1 };
}

function formatNumber(value: number): string {
  if (!Number.isFinite(value)) return "#VALUE!";
  const rounded = Math.round(value * 100) / 100;
  return String(rounded);
}

type Token =
  | { type: "number"; value: number }
  | { type: "ref"; value: string }
  | { type: "name"; value: string }
  | { type: "op"; value: string }
  | { type: "end" };

// #region engine.tokenize
/** 把公式文本切成 token：数字、单元格引用、函数名与运算符。 */
function tokenize(formula: string): Token[] {
  const tokens: Token[] = [];
  let index = 0;

  while (index < formula.length) {
    const char = formula[index];

    if (char === " ") {
      index += 1;
      continue;
    }

    if (/[0-9.]/.test(char)) {
      let end = index;
      while (end < formula.length && /[0-9.]/.test(formula[end])) end += 1;
      tokens.push({ type: "number", value: Number(formula.slice(index, end)) });
      index = end;
      continue;
    }

    if (/[A-Za-z]/.test(char)) {
      let end = index;
      while (end < formula.length && /[A-Za-z0-9]/.test(formula[end])) end += 1;
      const word = formula.slice(index, end).toUpperCase();
      tokens.push(/^[A-Z]+[0-9]+$/.test(word) ? { type: "ref", value: word } : { type: "name", value: word });
      index = end;
      continue;
    }

    if ("+-*/(),:".includes(char)) {
      tokens.push({ type: "op", value: char });
      index += 1;
      continue;
    }

    index += 1;
  }

  tokens.push({ type: "end" });
  return tokens;
}
// #endregion engine.tokenize

// #region engine.parse
type EvalValue = number | string[];

/** 递归下降求值：表达式 → 项 → 因子 → 主元（数字 / 引用 / 区域 / 函数）。 */
function evaluateFormula(cells: CellMap, formula: string, guard: Set<string>): number {
  const tokens = tokenize(formula);
  let position = 0;

  function current(): Token {
    return tokens[position] ?? { type: "end" };
  }

  function isOperator(token: Token, value: string): boolean {
    return token.type === "op" && token.value === value;
  }

  function readOperator(choices: string[]): string | null {
    const token = current();
    if (token.type !== "op" || !choices.includes(token.value)) return null;
    position += 1;
    return token.value;
  }

  function cellNumber(ref: string): number {
    if (guard.has(ref)) return 0;
    const raw = (cells[ref] ?? "").trim();
    if (!raw) return 0;
    if (raw.startsWith("=")) {
      guard.add(ref);
      const value = evaluateFormula(cells, raw.slice(1), guard);
      guard.delete(ref);
      return value;
    }
    const parsed = Number(raw.replace(/,/g, ""));
    return Number.isFinite(parsed) ? parsed : 0;
  }

  function expandRange(from: string, to: string): string[] {
    const start = parseCellRef(from);
    const end = parseCellRef(to);
    if (!start || !end) return [];
    const refs: string[] = [];
    for (let row = Math.min(start.row, end.row); row <= Math.max(start.row, end.row); row += 1) {
      for (let col = Math.min(start.col, end.col); col <= Math.max(start.col, end.col); col += 1) {
        refs.push(cellRefOf(col, row));
      }
    }
    return refs;
  }

  function numbersOf(value: EvalValue): number[] {
    return Array.isArray(value) ? value.map(cellNumber) : [value];
  }

  function parsePrimary(): EvalValue {
    const token = current();

    if (token.type === "number") {
      position += 1;
      return token.value;
    }

    if (token.type === "ref") {
      position += 1;
      const next = current();
      if (next.type === "op" && next.value === ":") {
        position += 1;
        const tail = current();
        if (tail.type !== "ref") return 0;
        position += 1;
        return expandRange(token.value, tail.value);
      }
      return cellNumber(token.value);
    }

    if (token.type === "name") {
      position += 1;
      if (isOperator(current(), "(")) {
        position += 1;
        const collected: number[] = [];
        while (current().type !== "end") {
          collected.push(...numbersOf(parseExpression()));
          const separator = current();
          if (separator.type === "op" && separator.value === ",") {
            position += 1;
            continue;
          }
          break;
        }
        if (isOperator(current(), ")")) position += 1;
        return applyFunction(token.value, collected);
      }
      return 0;
    }

    if (token.type === "op" && token.value === "(") {
      position += 1;
      const value = parseExpression();
      if (isOperator(current(), ")")) position += 1;
      return value;
    }

    return 0;
  }

  function parseTerm(): EvalValue {
    let value = parsePrimary();
    let operator = readOperator(["*", "/"]);
    while (operator) {
      const right = parsePrimary();
      const left = numbersOf(value)[0] ?? 0;
      const rightNumber = numbersOf(right)[0] ?? 0;
      value = operator === "*" ? left * rightNumber : rightNumber === 0 ? 0 : left / rightNumber;
      operator = readOperator(["*", "/"]);
    }
    return value;
  }

  function parseExpression(): EvalValue {
    let value = parseTerm();
    let operator = readOperator(["+", "-"]);
    while (operator) {
      const right = parseTerm();
      const left = numbersOf(value)[0] ?? 0;
      const rightNumber = numbersOf(right)[0] ?? 0;
      value = operator === "+" ? left + rightNumber : left - rightNumber;
      operator = readOperator(["+", "-"]);
    }
    return value;
  }

  const result = parseExpression();
  return numbersOf(result)[0] ?? 0;
}

function applyFunction(name: string, values: number[]): number {
  const usable = values.filter((value) => Number.isFinite(value));
  switch (name) {
    case "SUM":
      return usable.reduce((total, value) => total + value, 0);
    case "AVERAGE":
    case "AVG":
      return usable.length === 0 ? 0 : usable.reduce((total, value) => total + value, 0) / usable.length;
    case "MIN":
      return usable.length === 0 ? 0 : Math.min(...usable);
    case "MAX":
      return usable.length === 0 ? 0 : Math.max(...usable);
    case "COUNT":
      return usable.length;
    default:
      return 0;
  }
}
// #endregion engine.parse

// #region engine.evaluate
/** 单元格显示值：普通文本原样返回，公式返回计算结果。 */
export function displayCell(cells: CellMap, ref: string): string {
  const raw = (cells[ref] ?? "").trim();
  if (!raw.startsWith("=")) return raw;
  const value = evaluateFormula(cells, raw.slice(1), new Set([ref]));
  return formatNumber(value);
}

export function isFormula(cells: CellMap, ref: string): boolean {
  return (cells[ref] ?? "").trim().startsWith("=");
}
// #endregion engine.evaluate
